import { useCallback, useEffect, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';

import { trackEvent } from '@zep/logger/tracking';

import { useQuizList } from '../../../components/useQuizList';
import { useBuildFormContext } from '../../../form/schema';
import { aiSaveAnimation } from '../../../util/aiSaveAnimation';
import { type AiGenerateDockSourceType } from '../consts/aiGenerateDock.const';
import { useOptionalAiBuilderSessionStore } from '../stores/AiBuilderSessionProvider';
import { createAiBuilderClientRequestId } from '../stores/aiBuilderTurnRequests';
import {
  aiGenerateDockRunAtom,
  aiGenerateDockRunningCountAtom,
  aiGenerateDockRunningPhaseAtom,
} from '../stores/aiGenerateDockRun.atoms';
import { getRemainingQuizSlots } from '../utils/aiGenerateDockCount';
import { type AiGenerateDockRunOutcome } from '../utils/aiGenerateDockFeedback';
import { type AiGenerateDockRunInput } from '../utils/aiGenerateDockRunInput';
import {
  type AppliedQuizListChange,
  runAiGenerateDock,
} from '../utils/aiGenerateDockRunner';
import { toAiGeneratorRunEndOutcome } from '../utils/aiGenerateDockTracking';
import {
  type AiGeneratorAbortReason,
  type AiGeneratorOpenedPlace,
  type AiGeneratorRunTrigger,
} from '../utils/events.quiz-ai-generator';
import {
  type RegisterSource,
  registerSource,
  type StreamTurn,
  streamTurn,
} from '../utils/serverAiBuilderChatTransport';

import { useAiBuilderBuilderContext } from './useAiBuilderBuilderContext';
import { useAiBuilderChatAvailability } from './useAiBuilderChatAvailability';

export type AiGenerateDockTransport = {
  streamTurn: StreamTurn;
  registerSource: RegisterSource;
};

const DEFAULT_TRANSPORT: AiGenerateDockTransport = {
  streamTurn,
  registerSource,
};

/**
 * 생성기 실행 — 한 번에 하나, 채팅 턴과 동시에 돌지 않는다.
 *
 * 가드(사용 가능 · 남은 칸 · 세션 · 진행 중 작업) → 작업 표시 'generator' · 실행 상태 generating →
 * 실행 → 실행 상태(실패 자리 또는 비움)·작업 표시 정리. 실행 흐름의 전이(start·phase·applied·settle)는
 * 이 훅이 보내고, 결과 반응은 onSettled 로 호출부에 넘긴다.
 */
/**
 * 실행 하나가 어떻게 끝났는지 — `applied` 는 문항이 목록에 한 번이라도 들어왔다는 뜻이다.
 * `input` 은 이 실행이 등록한 자료(registeredSource)를 실은 입력이라, 그대로 다시 실행하면
 * 같은 세션에서는 자료를 다시 올리지 않는다.
 */
export type AiGenerateDockSettledRun = {
  input: AiGenerateDockRunInput;
  applied: boolean;
};

export type AiGenerateDockRunOptions = {
  /**
   * 실행을 시작하기 직전마다(처음 제출·실패 카드 다시 시도·안내 다시 시도 모두) 그 입력을 받는다 — 같은 세션을 쓰는
   * 다른 미완료 작업(사전 등록)을 실행보다 먼저 끊으려고 둔다.
   */
  onBeforeRun?: (input: AiGenerateDockRunInput) => void;
  /** 실행이 끝날 때마다(다시 시도 포함) 결과와 그 실행을 받는다 — 토스트·안내·입력 비우기는 호출부 몫. */
  onSettled?: (
    outcome: AiGenerateDockRunOutcome,
    run: AiGenerateDockSettledRun,
  ) => void;
  transport?: AiGenerateDockTransport;
  /** 입력창에서 고른 탭 — 실행 계측에만 쓴다. 자료 없이 실행하면 실제 종류(sourceType)와 다르다. */
  selectedTab?: AiGenerateDockSourceType;
  /** 무료 체험 남은 횟수 — 실행 계측에만 쓴다. 한도가 없거나 모르면 -1. */
  remainingUsage?: number;
  /** 마지막으로 사용자가 입력창을 연 표면 — 실행 계측에만 쓴다. */
  openedPlace?: AiGeneratorOpenedPlace;
};

export const useAiGenerateDockRun = ({
  onBeforeRun,
  onSettled,
  transport = DEFAULT_TRANSPORT,
  selectedTab = 'query',
  remainingUsage = -1,
  openedPlace = '',
}: AiGenerateDockRunOptions = {}) => {
  const { canUseAiBuilderChat } = useAiBuilderChatAvailability();
  const sessionStore = useOptionalAiBuilderSessionStore();
  const { getValues, setValue } = useBuildFormContext();
  const { replaceQuizList } = useQuizList();
  const builderContext = useAiBuilderBuilderContext();
  const dispatchRun = useSetAtom(aiGenerateDockRunAtom);
  const runningCount = useAtomValue(aiGenerateDockRunningCountAtom);
  const runningPhase = useAtomValue(aiGenerateDockRunningPhaseAtom);
  const controllerRef = useRef<AbortController | null>(null);
  // 누가 끊었나 — 끝 이벤트가 사용자 중단과 화면 이탈을 가른다. abort(reason) 은 fetch 가 AbortError 대신
  // 그 값으로 거절하게 바꾸므로 쓰지 않고 따로 적어 둔다.
  const abortReasonsRef = useRef(
    new WeakMap<AbortController, AiGeneratorAbortReason>(),
  );
  const abortCurrent = useCallback((reason: AiGeneratorAbortReason) => {
    const controller = controllerRef.current;
    if (!controller) return;
    abortReasonsRef.current.set(controller, reason);
    controller.abort();
  }, []);
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;
  const onBeforeRunRef = useRef(onBeforeRun);
  onBeforeRunRef.current = onBeforeRun;
  const trackingStateRef = useRef({ selectedTab, remainingUsage, openedPlace });
  trackingStateRef.current = { selectedTab, remainingUsage, openedPlace };
  // 실패 자리의 retry 가 onSettled 까지 같은 실행 경로를 타도록 최신 run 을 가리킨다.
  const runRef = useRef<
    (
      input: AiGenerateDockRunInput,
      trigger: AiGeneratorRunTrigger,
    ) => Promise<AiGenerateDockRunOutcome>
  >(async () => ({ kind: 'aborted' }));

  // 언마운트로 끊긴 실행은 사용자가 중단한 게 아니다 — onSettled(「중단했습니다」 등)를 부르지 않는다.
  const mountedRef = useRef(false);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      abortCurrent('unmount');
    };
  }, [abortCurrent]);

  const applyQuizList = useCallback(
    (change: AppliedQuizListChange) => {
      replaceQuizList(change.nextQuizList);
      setValue('quizList', change.nextQuizList, {
        shouldDirty: true,
        shouldTouch: true,
      });
      if (change.animationQuizIds.length > 0) {
        aiSaveAnimation(change.animationQuizIds, {
          scrollTargetId: change.scrollTargetQuizId,
        });
      }
    },
    [replaceQuizList, setValue],
  );

  const execute = useCallback(
    async (
      input: AiGenerateDockRunInput,
      clientRequestId: string,
    ): Promise<{
      outcome: AiGenerateDockRunOutcome;
      applied: boolean;
      input: AiGenerateDockRunInput;
      /** 이 실행이 확보한 세션 id. 세션 전에 끝났으면 빈 문자열. */
      sessionId: string;
      /** 끊겼다면 누가 끊었나. 끊기지 않았으면 빈 문자열. */
      abortReason: AiGeneratorAbortReason | '';
    }> => {
      const notApplied = (outcome: AiGenerateDockRunOutcome) => ({
        outcome,
        applied: false,
        input,
        sessionId: '',
        abortReason: '' as const,
      });
      if (!canUseAiBuilderChat)
        return notApplied({ kind: 'blocked', reason: 'unavailable' });

      const currentQuizCount = getValues('quizList').length;
      const remaining = getRemainingQuizSlots(currentQuizCount);
      if (remaining <= 0)
        return notApplied({ kind: 'blocked', reason: 'no_slots' });

      const port = sessionStore?.getPort();
      if (!sessionStore || !port) {
        return notApplied({ kind: 'blocked', reason: 'no_session' });
      }
      // 이 화면에서 이미 실행 중이면 새로 시작하지 않는다 — 세션 작업 잠금은 같은 작업(generator)의 재진입은 허용한다.
      if (controllerRef.current) {
        return notApplied({ kind: 'blocked', reason: 'busy' });
      }
      if (!sessionStore.tryBeginOperation('generator')) {
        return notApplied({ kind: 'blocked', reason: 'busy' });
      }

      const count = Math.min(input.count, remaining);
      const quizType = input.quizTypes[0];
      const controller = new AbortController();
      controllerRef.current = controller;
      // 등록을 재사용할 수 있으면 곧바로 생성 단계다 — 세션이 바뀌었으면 실행이 되돌린다.
      const reusesSource =
        input.sourceType === 'query' || input.registeredSource !== undefined;
      // 실행 id 는 서버 턴과 잇는 요청 id 를 그대로 쓴다 — 실행마다 새로 만든다.
      const runId = clientRequestId;
      dispatchRun({
        type: 'start',
        runId,
        phase: reusesSource ? 'generating' : 'preparing_source',
        count,
        startNumber: currentQuizCount + 1,
        quizType,
      });

      let outcome: AiGenerateDockRunOutcome = { kind: 'aborted' };
      let applied = false;
      let runSessionId = '';
      let settledInput = input;
      try {
        outcome = await runAiGenerateDock(
          { ...input, count },
          {
            draftInstanceId: port.draftInstanceId,
            builderContext,
            signal: controller.signal,
            createClientRequestId: () => clientRequestId,
            ensureSession: port.ensureSession,
            getQuizList: () => getValues('quizList'),
            applyQuizList: change => {
              // 문항이 들어오는 순간 대기 카드를 걷는다 — 같은 자리에 실제 카드가 그려진다.
              applied = true;
              dispatchRun({ type: 'applied', runId });
              applyQuizList(change);
            },
            streamTurn: transport.streamTurn,
            registerSource: transport.registerSource,
            onPhase: phase => dispatchRun({ type: 'phase', runId, phase }),
            onSessionReady: sessionId => {
              runSessionId = sessionId;
            },
            onSourceRegistered: registeredSource => {
              settledInput = { ...input, registeredSource };
            },
          },
        );
        return {
          outcome,
          applied,
          input: settledInput,
          sessionId: runSessionId,
          abortReason: abortReasonsRef.current.get(controller) ?? '',
        };
      } finally {
        if (controllerRef.current === controller) controllerRef.current = null;
        // 실행 상태 전이는 여기 한 곳 — 통째 실패면 실패 자리, 그 밖은 비운다. 문항이 이미 들어왔으면
        // 통째 실패가 아니다 — 실패 카드를 두면 같은 요청을 다시 보내 비슷한 문항이 겹쳐 생긴다.
        dispatchRun({
          type: 'settle',
          runId,
          failed:
            outcome.kind === 'failed' && !applied
              ? {
                  count,
                  quizType,
                  retry: (retryCount: number) =>
                    void runRef.current(
                      { ...settledInput, count: retryCount },
                      'failed_card_retry',
                    ),
                }
              : null,
        });
        sessionStore.endOperation('generator');
      }
    },
    [
      applyQuizList,
      builderContext,
      canUseAiBuilderChat,
      getValues,
      sessionStore,
      dispatchRun,
      transport.registerSource,
      transport.streamTurn,
    ],
  );

  /** 실행하고 결과를 onSettled 로 알린다. 결과는 그대로 돌려준다. */
  const run = useCallback(
    async (
      input: AiGenerateDockRunInput,
      trigger: AiGeneratorRunTrigger = 'submit',
    ) => {
      onBeforeRunRef.current?.(input);
      const startedAt = Date.now();
      const clientRequestId = createAiBuilderClientRequestId();
      const { openedPlace: place } = trackingStateRef.current;
      trackEvent('quiz_ai_generator_run_submit', {
        place,
        trigger,
        selected_tab: trackingStateRef.current.selectedTab,
        source_type: input.sourceType,
        requested_count: input.count,
        quiz_types: input.quizTypes,
        difficulty: String(input.difficulty),
        has_topic: input.topic.trim().length > 0,
        remaining_slot_count: getRemainingQuizSlots(
          getValues('quizList').length,
        ),
        remaining_usage: trackingStateRef.current.remainingUsage,
        client_request_id: clientRequestId,
        destination: 'quiz_list',
      });
      const {
        outcome,
        applied,
        input: settledInput,
        sessionId: runSessionId,
        abortReason,
      } = await execute(input, clientRequestId);
      // 화면을 떠나 끊긴 실행도 끝으로 센다 — 결과는 aborted.
      const ended = toAiGeneratorRunEndOutcome(outcome);
      trackEvent('quiz_ai_generator_run_end', {
        place,
        trigger,
        source_type: input.sourceType,
        result: ended.result,
        // 끊긴 실행은 누가 끊었는지가 사유다 — 사용자 중단만 중단율에 넣는다.
        reason: ended.result === 'aborted' ? abortReason : ended.reason,
        requested_count: input.count,
        created_count: ended.created_count,
        failed_count: ended.failed_count,
        applied,
        latency_ms: Date.now() - startedAt,
        // 실행이 확보한 세션이 먼저다 — 실행 도중 화면을 떠나면 포트는 이미 바뀌었거나 비었다.
        session_id:
          runSessionId || sessionStore?.getPort()?.getSessionId() || '',
        client_request_id: clientRequestId,
      });
      // 무료 소진 거절 — 채팅과 같은 업셀 퍼널의 시작점이다.
      if (
        outcome.kind === 'rejected' &&
        outcome.rejection.kind === 'quota_exceeded'
      ) {
        trackEvent('quiz_ai_builder_usage_limit_view', {
          place: 'generator_dock',
          remaining: outcome.rejection.usage?.remaining ?? 0,
          limit: outcome.rejection.usage?.limit ?? -1,
        });
      }
      if (mountedRef.current) {
        onSettledRef.current?.(outcome, { input: settledInput, applied });
      }
      return outcome;
    },
    [execute, getValues, sessionStore],
  );
  runRef.current = run;

  const cancel = useCallback(() => abortCurrent('user_cancel'), [abortCurrent]);

  return {
    canUse: canUseAiBuilderChat,
    runningCount,
    runningPhase,
    run,
    cancel,
  };
};
